import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import useContractBreakdown from '../../hooks/useContractBreakdown';
import ContractBreakdownSummary from '../ContractBreakdownSummary';

/* ═══════════════════════════════════════════════════════════════════════════
   Job Costs — shared token components + helpers (warm-cream + sage)
   Used by both the top-level Job Costs dashboard (JobCostDashboardPage) and the
   project-detail Job Costs tab (JobCostTab). Consumes normalized shapes built
   by each page from the real CE-sync endpoints.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── money + format ─────────────── */

export const fmt$ = (n, { signed = false, whole = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  // whole: rounded to the dollar, for the KPI tile faces (Duncan 2026-10-04 — cents are
  // noise up top); the phase breakdown and drop-downs keep full cents.
  const num = whole ? Math.round(+n) || 0 : +n;
  const dp = whole ? 0 : 2;
  const abs = Math.abs(num).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: dp, maximumFractionDigits: dp });
  if (num < 0) return '-' + abs;
  if (signed && num > 0) return '+' + abs;
  return abs;
};

export const fmt$c = (n, { signed = false } = {}) => {
  if (n == null || Number.isNaN(+n)) return '—';
  const num = +n;
  const a = Math.abs(num);
  let s;
  if (a >= 1e6) { const m = a / 1e6; s = '$' + (m >= 100 ? m.toFixed(0) : m.toFixed(1)) + 'M'; }
  else if (a >= 1e3) s = '$' + Math.round(a / 1e3) + 'k';
  else s = '$' + Math.round(a);
  if (num < 0) return '-' + s;
  if (signed && num > 0) return '+' + s;
  return s;
};

export const fmtPct = (n, dp = 1) => (n == null || Number.isNaN(+n) ? '—' : (Math.round(+n * Math.pow(10, dp)) / Math.pow(10, dp)).toFixed(dp) + '%');

// A date-only value ('2026-10-04', or the server's '2026-10-04T00:00:00.000Z' for a DATE
// column) is that calendar day; read as a UTC instant it showed a day early in Central (PSR #37).
const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z)?$/;

export const fmtDate = (d) => {
  if (!d) return '—';
  const day = typeof d === 'string' && DAY_ONLY.exec(d);
  const dt = d instanceof Date ? d : day ? new Date(+day[1], +day[2] - 1, +day[3]) : new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ─────────────── cost-health ramp (pct = cost/budget, RATIO 0–1) ─────────────── */

// Cost-health tiers. These thresholds are the ONE source of truth for both the
// health badge AND the dashboard filter (filterBucket derives from this below),
// so a job's badge can never disagree with the bucket the filter drops it into.
// Strict boundaries at exactly-100%: anything over (even a penny) is Over Budget,
// EXACTLY at budget (cost === budget → pct 1.0) is a calm neutral "At Budget",
// and anything under (even 99.9999%) is At Risk down to 85%. x/x === 1.0 exactly
// in IEEE754, so assumed-at-budget items (cost set = budget) and any real line
// where cost equals budget both land cleanly in 'atbudget'.
//   over >100% · at-budget =100% · at-risk 85–<100% · on-track 50–<85% · under <50%
export const healthKey = (pct) => {
  if (pct == null) return 'none';
  if (pct > 1) return 'over';          // strictly over budget → Over Budget
  if (pct >= 1) return 'atbudget';     // exactly at budget (pct === 1) → At Budget (neutral)
  if (pct >= 0.85) return 'at';        // 85–<100% → At Risk
  if (pct >= 0.5) return 'on';         // 50–<85%  → On Track
  return 'under';                      // <50%     → Under Budget
};
const HEALTH = {
  over: { label: 'Over Budget', tone: 'var(--danger)' },
  atbudget: { label: 'At Budget', tone: 'var(--text-3)' },
  at: { label: 'At Risk', tone: 'var(--warning)' },
  on: { label: 'On Track', tone: 'var(--success)' },
  under: { label: 'Under Budget', tone: 'var(--success)' },
  none: { label: 'No Budget', tone: 'var(--text-4)' },
};
export const healthMeta = (pct) => ({ key: healthKey(pct), ...HEALTH[healthKey(pct)] });
export const healthTone = (pct) => HEALTH[healthKey(pct)].tone;
export const spentColor = (pct) => {
  const k = healthKey(pct);
  if (k === 'none') return 'var(--text-4)';
  if (k === 'under' || k === 'atbudget') return 'var(--text-1)';
  return HEALTH[k].tone;
};
const healthBadgeColors = (pct) => {
  const k = healthKey(pct);
  if (k === 'over') return { fg: 'var(--danger)', soft: 'var(--danger-soft)', ringTone: 'var(--danger)' };
  if (k === 'at') return { fg: 'var(--warning)', soft: 'var(--warning-soft)', ringTone: 'var(--warning)' };
  if (k === 'on' || k === 'under') return { fg: 'var(--success)', soft: 'var(--success-soft)', ringTone: 'var(--success)' };
  return { fg: 'var(--text-3)', soft: 'var(--bg-surface-2)', ringTone: 'var(--border-strong)' };
};
export const overTone = (v) => (v > 0.5 ? 'var(--danger)' : v < -0.5 ? 'var(--success)' : 'var(--text-3)');
// Dashboard filter buckets — derived from healthKey so the filter and the badge
// can never drift apart: over (>100%), at-risk (85–100%, incl. exactly-at-budget),
// on-track (<85%). (No job is ever exactly at budget, so 'atbudget' here is a
// near-dead path — grouped with at-risk as the fully-consumed end.)
export const filterBucket = (pct) => {
  const k = healthKey(pct);
  if (k === 'over') return 'over';
  if (k === 'atbudget' || k === 'at') return 'at-risk';
  return 'on-track'; // on, under, none
};

/* ─────────────── assumed-at-budget cost codes ─────────────── */

export const ASSUMED_AT_BUDGET_CODES = new Set(['910', '911', '912', '913', '914', '915', '916', '917', '918', '391', '362']);
// Except a job's own phase, which reads its real cost (Duncan 2026-10-20): a P job's phase 7 (Performance
// Management) and a W job's phase 8 (Warranty -- "for W jobs we can't be assuming anything in phase 8").
// KEEP IN SYNC with IS_ASSUMED in server/jobCostQueries.js. jobId: the job the items belong to.
const OWN_PHASES = [[/-P-\d+$/i, '7'], [/-W-\d+$/i, '8']];
const isOwnPhase = (jobId, phase) => {
  const job = String(jobId || '').trim();
  const ph = String(phase || '').trim().replace(/^0+/, '');
  return OWN_PHASES.some(([re, own]) => re.test(job) && ph === own);
};
export const applyBudgetAssumption = (items, jobId) =>
  (items || []).map((li) => {
    const cat = String(li.category || '').trim();
    if (ASSUMED_AT_BUDGET_CODES.has(cat) && !isOwnPhase(jobId || li.job_id, li.phase)) {
      const budget = parseFloat(li.cost_budget || 0);
      return { ...li, cost: budget, cost_overrun: 0, _assumed: true };
    }
    return li;
  });

/* ─────────────── normalizers: real CE shapes → table/chart shapes ─────────────── */

// rawPhases: [{phase, phase_name, total_budget, total_cost, total_committed, total_overrun}]
// adjLineItems: applyBudgetAssumption() output [{phase, category, category_name, cost_budget, cost, cost_committed, cost_overrun, _assumed}]
export const buildPhases = (rawPhases, adjLineItems) => {
  const nonZero = (v) => parseFloat(v || 0) !== 0;
  return (rawPhases || [])
    .map((p) => {
      const items = (adjLineItems || []).filter((li) => li.phase === p.phase);
      const codes = items
        .filter((li) => nonZero(li.cost_budget) || nonZero(li.cost) || nonZero(li.cost_committed) || nonZero(li.cost_overrun))
        .map((li) => ({
          cat: li.category_name || li.category || '—',
          code: li.category || '',
          budget: parseFloat(li.cost_budget || 0),
          actual_cost: parseFloat(li.cost || 0),
          committed: parseFloat(li.cost_committed || 0),
          over_under: parseFloat(li.cost_overrun || 0),
          assumed: !!li._assumed,
        }));
      // recompute phase totals from adjusted items when present
      const sum = (k) => codes.reduce((s, c) => s + c[k], 0);
      const budget = codes.length ? sum('budget') : parseFloat(p.total_budget || 0);
      const actual_cost = codes.length ? sum('actual_cost') : parseFloat(p.total_cost || 0);
      const committed = codes.length ? sum('committed') : parseFloat(p.total_committed || 0);
      const over_under = codes.length ? actual_cost - budget : parseFloat(p.total_overrun || 0);
      return { no: p.phase, name: p.phase_name || `Phase ${p.phase}`, budget, actual_cost, committed, over_under, codes };
    })
    .filter((p) => p.budget !== 0 || p.actual_cost !== 0 || p.committed !== 0 || p.over_under !== 0);
};

// rows: [{period_month (1-12), period_year, total_cost}]
// total_cost is CUMULATIVE (inception-to-date through that month). The chart shows
// per-month SPEND, so we difference each month from the prior month's cumulative
// (first month's spend = its own cumulative — nothing earlier to subtract). This
// makes the bars sum exactly to the current cumulative total. Sorted defensively
// so the differencing is correct regardless of row order. `cumulative` is kept for
// tooltips / a future cumulative toggle.
// hoursRows: cumulative hours per month (job_hours_detail monthly summary);
// invoicedRows: CE invoiced per month. Either may be null (not loaded / unavailable).
const ymKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
const prevYm = (key) => { const [y, m] = key.split('-').map(Number); return m === 1 ? ymKey(y - 1, 12) : ymKey(y, m - 1); };
export const buildMonthly = (rows, hoursRows, invoicedRows) => {
  const src = [...(rows || [])].sort(
    (a, b) => (a.period_year - b.period_year) || (a.period_month - b.period_month)
  );
  const hoursCum = Array.isArray(hoursRows) && hoursRows.length
    ? new Map(hoursRows.map((h) => [ymKey(h.period_year, h.period_month), parseFloat(h.hours || 0)])) : null;
  const billedBy = Array.isArray(invoicedRows) ? new Map(invoicedRows.map((r) => [r.period, Number(r.invoiced) || 0])) : null;
  return src.map((m, i) => {
    const key = ymKey(m.period_year, m.period_month);
    const cumulative = parseFloat(m.total_cost || 0);
    const prev = i > 0 ? parseFloat(src[i - 1].total_cost || 0) : 0;
    // Hours used IN the month = this month's cumulative minus last month's. Only when
    // last month is stored: the first month's cumulative is everything worked BEFORE
    // it too (BRYAN-D-03: 2,937.5 h by Nov '23 vs ~50-300 h a month after), and a
    // month missing from the history must leave a gap, not lump months into a spike.
    let hours = null;
    if (hoursCum && hoursCum.has(key) && hoursCum.has(prevYm(key))) {
      hours = hoursCum.get(key) - hoursCum.get(prevYm(key));
    }
    return {
      month: `${MONTH_ABBR[+m.period_month] || ''} '${String(m.period_year).slice(-2)}`,
      key,
      cost: cumulative - prev, // per-month spend (bars sum to the total)
      cumulative,
      billed: billedBy ? (billedBy.get(key) || 0) : null, // what CE invoiced that month
      hours,
    };
  });
};

/* ─────────────── inline SVG icons ─────────────── */

export const JcIcon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      {d}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return p(<><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></>);
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'download':
      return p(<><path d="M12 4v11" /><path d="m7 10 5 5 5-5" /><path d="M5 20h14" /></>);
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    case 'archive':
      return p(<><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></>);
    case 'external':
      return p(<><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></>);
    case 'note':
      return p(<><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2Z" /><path d="M8 9h8M8 13h5" /></>);
    case 'note-plus':
      return p(<><path d="M21 11.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v15l4-4h6" /><path d="M18 14v6M15 17h6" /></>);
    case 'sync':
      return p(<><path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3" /><path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3" /><path d="M21 3v5h-5M3 21v-5h5" /></>);
    case 'pencil':
      return p(<><path d="M4 20h4L18.5 9.5a2.83 2.83 0 0 0-4-4L4 16v4" /><path d="m13.5 6.5 4 4" /></>);
    default:
      return null;
  }
};

/* ─────────────── health badge ─────────────── */

export const HealthBadge = ({ pct, size = 'md' }) => {
  const b = healthBadgeColors(pct);
  const h = healthMeta(pct);
  const sm = size === 'sm';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: sm ? 19 : 22, padding: sm ? '0 8px' : '0 10px', borderRadius: 999, fontSize: sm ? 10.5 : 11.5, fontWeight: 600, letterSpacing: '-0.005em', whiteSpace: 'nowrap', color: b.fg, background: b.soft, border: `1px solid ${ring(b.ringTone)}` }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: b.fg, flex: '0 0 auto' }} />
      {h.label}
    </span>
  );
};

/* ─────────────── detailed KPI cards (two rows of 4) ─────────────── */

// A tile that opens a drill-down (Actual Cost → spent by phase) renders as a button
// with a small chevron; its popover floats below it without moving the grid. Hover
// previews it, click pins it (and is the only way in on a touch screen).
const KpiCard = ({ label, value, sub, valueColor, fill, ringColor, muted, onToggle, onHoverStart, onHoverEnd, expanded, popover, popoverId }) => {
  const box = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, padding: '14px 16px', borderRadius: 10, background: fill || 'var(--bg-surface)', border: `1px solid ${ringColor || 'var(--border-default)'}`, boxShadow: 'var(--shadow-xs)' };
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.055em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        {onToggle && <span aria-hidden="true" style={{ display: 'inline-flex', flex: '0 0 auto', color: expanded ? 'var(--accent-text)' : 'var(--text-4)', transition: 'transform 160ms ease', transform: expanded ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={13} /></span>}
      </div>
      <div className="mono" style={{ fontSize: 22.5, fontWeight: 800, lineHeight: 1.05, color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div className="mono" style={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{sub}</div>}
    </>
  );
  if (!onToggle) return <div style={box}>{body}</div>;
  return (
    <div data-kpi-drill="" onMouseEnter={onHoverStart} onMouseLeave={onHoverEnd} style={{ position: 'relative', minWidth: 0, display: 'flex' }}>
      <button type="button" className="jc-kpi-drill" onClick={onToggle} aria-expanded={expanded} aria-controls={popoverId} style={{ ...box, flex: 1, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', ...(expanded ? { border: '1px solid var(--accent)', boxShadow: '0 0 0 3px var(--accent-ring)' } : null) }}>
        {body}
      </button>
      {expanded && popover}
    </div>
  );
};

/* A P, E or M job's Contract Value tile (Duncan 2026-10-20). The job has no project page, so an admin
   sets its contract here -- inside the job, never from the list ("I don't wanna be able to edit
   anything from just a list"). What's typed is the base contract; CE's change orders are added on top.
   contract: { set, change_orders, value, canEdit, onSave(amount | null) }. Same face as KpiCard. */
const editBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 8px', borderRadius: 6, font: 'inherit', fontSize: 12, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--text-2)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', cursor: 'pointer' };
const ContractValueTile = ({ contract }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const co = contract.change_orders || 0;
  const start = () => { setDraft(contract.set == null ? '' : Number(contract.set).toFixed(2)); setErr(null); setEditing(true); };
  const save = async () => {
    const raw = draft.replace(/[$,\s]/g, '');
    if (raw !== '' && !/^\d+(\.\d{1,2})?$/.test(raw)) { setErr('Enter a dollar amount, like 21262.00'); return; }
    setBusy(true); setErr(null);
    try { await contract.onSave(raw === '' ? null : Number(raw)); setEditing(false); } catch (e) { setErr(e.message || 'Couldn’t save. Try again.'); } finally { setBusy(false); }
  };
  const sub = contract.set == null
    ? (co ? `${fmt$(co)} CE change orders · no contract set` : 'No contract set')
    : co ? `${fmt$(contract.set)} + ${fmt$(co)} CE change orders` : `${fmt$(contract.set)} set in Job Costs`;
  return (
    <div onKeyDown={(e) => { if (editing && e.key === 'Escape') { e.stopPropagation(); setEditing(false); } }}
      style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, padding: '14px 16px', borderRadius: 10, background: 'var(--bg-surface)', border: `1px solid ${editing ? 'var(--accent)' : 'var(--border-default)'}`, boxShadow: editing ? '0 0 0 3px var(--accent-ring)' : 'var(--shadow-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, minHeight: 18, fontSize: 12, fontWeight: 700, letterSpacing: '0.055em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>Contract Value</span>
        {contract.canEdit && !editing && (
          <button type="button" className="jc-kpi-edit" onClick={start} style={{ ...editBtn, margin: '-4px -6px -4px 0' }}><JcIcon name="pencil" size={12} sw={1.8} />Edit</button>
        )}
      </div>
      {editing ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-3)' }}>$</span>
            <input autoFocus inputMode="decimal" aria-label="Contract value" value={draft} placeholder="21262.00" disabled={busy}
              onChange={(e) => { setDraft(e.target.value); setErr(null); }} onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              className="mono" style={{ flex: 1, minWidth: 0, height: 32, padding: '0 9px', borderRadius: 7, fontSize: 15, fontWeight: 700, color: 'var(--text-1)', background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" onClick={save} disabled={busy} style={{ ...editBtn, height: 28, padding: '0 12px', color: '#fff', background: 'var(--accent)', border: '1px solid color-mix(in oklch, var(--accent) 50%, black)', cursor: busy ? 'default' : 'pointer' }}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} style={{ ...editBtn, height: 28, padding: '0 12px' }}>Cancel</button>
          </div>
          <div className="mono" style={{ fontSize: 12.5, lineHeight: 1.4, color: err ? 'var(--danger)' : 'var(--text-3)' }}>
            {err || (co ? `+ ${fmt$(co)} CE change orders added on top` : 'CE change orders get added on top')}
          </div>
        </>
      ) : (
        <>
          <div className="mono" style={{ fontSize: 22.5, fontWeight: 800, lineHeight: 1.05, color: contract.value == null ? 'var(--text-4)' : 'var(--text-1)', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
            {contract.value == null ? '—' : fmt$(contract.value, { whole: true })}
          </div>
          <div className="mono" style={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{sub}</div>
        </>
      )}
    </div>
  );
};

/* Actual Cost drill-down — the budget headers (phases) behind the spent %, most over
   budget IN DOLLARS first: a 117% phase on $420k drives the job less than 101.5% on
   $11.7M. Percent big, dollars small, like the tiles; every cost code stays in the
   Phase Breakdown table this links down to. */
const PHASE_DRIVERS_MAX = 8;
const KPI_DRILL_CSS = [
  '.jc-kpi-drill:hover{border-color:color-mix(in oklch,var(--accent) 45%,var(--border-default))!important}',
  '.jc-kpi-drill:focus-visible{outline:none;box-shadow:0 0 0 3px var(--accent-ring)!important}',
  '.jc-kpi-more{display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 11px;border-radius:8px;font:inherit;font-size:13px;font-weight:500;color:var(--text-2);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer}',
  '.jc-kpi-more:hover{border-color:color-mix(in oklch,var(--accent) 35%,var(--border-default));color:var(--accent-text);background:var(--accent-soft)}',
  '.jc-kpi-more:focus-visible{outline:none;box-shadow:0 0 0 3px var(--accent-ring)}',
].join('');
const PhaseDrivers = ({ id, phases, onSeeAll, onClose }) => {
  const rows = [...phases].sort((a, b) => b.over_under - a.over_under);
  const shown = rows.slice(0, PHASE_DRIVERS_MAX);
  return (
    <div id={id} role="dialog" aria-label="Spent by phase" tabIndex={-1} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40, width: 340, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 14px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
          Spent by phase <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>· most over budget first</span>
        </span>
        <button type="button" aria-label="Close" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {shown.map((p, i) => {
          const pct = p.budget > 0 ? p.actual_cost / p.budget : null;
          return (
            <div key={p.no} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>Phase {p.no}</div>
              </div>
              <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
                <div className="mono" style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.15, color: pct == null ? 'var(--text-4)' : spentColor(pct), fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{pct == null ? '—' : fmtPct(pct * 100)}</div>
                <div className="mono" style={{ fontSize: 12, color: overTone(p.over_under), fontVariantNumeric: 'tabular-nums' }}>{fmt$(p.over_under, { signed: true })}</div>
              </div>
            </div>
          );
        })}
        {rows.length > shown.length && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-3)' }}>+ {rows.length - shown.length} more phases</div>
        )}
      </div>
      {onSeeAll && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
          <button type="button" className="jc-kpi-more" onClick={() => { onClose(); onSeeAll(); }}>
            View phase breakdown <JcIcon name="chevron-d" size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

/* Travel drill-down — the job's travel cost summary, laid out like Colin's manual PSR
   ("Total Job Travel Cost Summary"): all six travel codes in code order, even the
   empty ones, then a totals row. Percent used big, dollars small, like the tiles. */
const TRAVEL_CODES = [['370', 'Air'], ['371', 'Miles'], ['372', 'Hotel'], ['373', 'Meals'], ['374', 'Rental/Gas'], ['375', 'Misc']];

/* Labor hours — Colin's PSR "Total Job Hours Summary": hours per labor cost code,
   budget / used / remaining, in his code order. A labor code not on his list is
   appended after them, so nothing CE reports is ever dropped. */
const HOURS_CODES = [['301', 'Business Development'], ['311', 'Project Development'], ['312', 'In House MEP Engineering'], ['331', 'Project Management'], ['332', 'Commissioning'], ['333', 'Construction Management'], ['334', 'Operations Management'], ['341', 'E3 Install Labor']];
export const fmtHrs = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Rows arrive per phase × code; the summary is per code across the whole job.
export const hoursByCode = (rows) => {
  const by = {};
  (rows || []).forEach((r) => {
    const k = String(r.category || '').trim();
    if (!k) return;
    if (!by[k]) by[k] = { name: r.category_name || '', budget: 0, used: 0 };
    by[k].budget += parseFloat(r.hours_budget || 0);
    by[k].used += parseFloat(r.hours || 0);
  });
  const listed = HOURS_CODES.map(([code, name]) => ({ code, name: (by[code] && by[code].name) || name, budget: by[code] ? by[code].budget : 0, used: by[code] ? by[code].used : 0 }));
  const extra = Object.keys(by).filter((k) => !HOURS_CODES.some(([c]) => c === k)).sort()
    .map((k) => ({ code: k, name: by[k].name || `Code ${k}`, budget: by[k].budget, used: by[k].used }));
  return [...listed, ...extra];
};
/* The same hours grouped by discipline: CE's phase (3 = E3 Lighting, 4 = Construction…), so
   lighting PM (3.331) and construction PM (4.331) aren't added together (#62). Codes keep
   Colin's order inside each group; codes and groups with no hours are left out. The hours
   pull carries phase numbers only, so names come from the job's cost phases (phaseNames:
   number → name), else "Phase N". */
const phaseKey = (v) => String(v == null ? '' : v).trim().replace(/^0+(?=\d)/, '');
export const phaseNamesOf = (phases) => Object.fromEntries((phases || []).map((p) => [phaseKey(p.no), p.name]));
export const hoursByDiscipline = (rows, phaseNames = {}) => {
  const groups = new Map();
  (rows || []).forEach((r) => {
    const code = String(r.category || '').trim();
    const budget = parseFloat(r.hours_budget || 0) || 0;
    const used = parseFloat(r.hours || 0) || 0;
    if (!code || (!budget && !used)) return;
    const no = phaseKey(r.phase);
    if (!groups.has(no)) groups.set(no, { no, name: phaseNames[no] || (no ? `Phase ${no}` : 'No phase'), by: {} });
    const by = groups.get(no).by;
    if (!by[code]) by[code] = { code, name: r.category_name || '', budget: 0, used: 0 };
    by[code].budget += budget;
    by[code].used += used;
  });
  const rank = (code) => { const i = HOURS_CODES.findIndex(([c]) => c === code); return i < 0 ? HOURS_CODES.length : i; };
  return [...groups.values()]
    .sort((a, b) => a.no.localeCompare(b.no, undefined, { numeric: true }))
    .map((g) => {
      const lines = Object.values(g.by)
        .map((c) => ({ ...c, name: c.name || (HOURS_CODES.find(([k]) => k === c.code) || [])[1] || `Code ${c.code}` }))
        .sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code, undefined, { numeric: true }));
      return { no: g.no, name: g.name, lines, budget: lines.reduce((a, c) => a + c.budget, 0), used: lines.reduce((a, c) => a + c.used, 0) };
    });
};
const HOURS_COLS = 'minmax(0, 1fr) 72px 72px 80px';
const HoursLine = ({ code, name, budget, used, total, sub }) => {
  const left = budget - used;
  const idle = !total && !sub && !budget && !used;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: HOURS_COLS, alignItems: 'baseline', gap: 10, padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', background: total ? 'var(--bg-surface-2)' : 'transparent', color: idle ? 'var(--text-4)' : sub ? 'var(--text-2)' : 'var(--text-1)' }}>
      <div style={{ minWidth: 0, fontSize: 13, fontWeight: total ? 700 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {code && <span className="mono" style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text-4)', marginRight: 7, fontVariantNumeric: 'tabular-nums' }}>{code}</span>}{name}
      </div>
      <span className="mono" style={{ textAlign: 'right', fontSize: 13, fontWeight: total ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{fmtHrs(budget)}</span>
      <span className="mono" style={{ textAlign: 'right', fontSize: 13, fontWeight: total ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{fmtHrs(used)}</span>
      <span className="mono" style={{ textAlign: 'right', fontSize: 13, fontWeight: total ? 800 : 600, fontVariantNumeric: 'tabular-nums', color: idle ? 'var(--text-4)' : left < 0 ? 'var(--danger)' : 'var(--text-2)' }}>{left < 0 ? `−${fmtHrs(-left)}` : fmtHrs(left)}</span>
    </div>
  );
};
const HoursSummary = ({ id, rows, phases, onClose }) => {
  const groups = hoursByDiscipline(rows, phaseNamesOf(phases));
  const total = groups.reduce((a, g) => ({ budget: a.budget + g.budget, used: a.used + g.used }), { budget: 0, used: 0 });
  // Right-aligned: Labor Hours is the last tile in its row (like Travel).
  return (
    <div id={id} role="dialog" aria-label="Labor hours summary" tabIndex={-1} style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40, width: 530, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 14px', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
          Labor hours summary <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>· by discipline</span>
        </span>
        <button type="button" aria-label="Close" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: HOURS_COLS, gap: 10, padding: '7px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
        <span>Cost code</span><span style={{ textAlign: 'right' }}>Budget</span><span style={{ textAlign: 'right' }}>Used</span><span style={{ textAlign: 'right' }}>Remaining</span>
      </div>
      {groups.map((g) => (
        <React.Fragment key={g.no}>
          <div style={{ padding: '8px 14px 6px', borderTop: '1px solid var(--border-subtle)', background: 'var(--accent-soft)', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {g.name}{g.no ? <span style={{ fontWeight: 500 }}> · phase {g.no}</span> : null}
          </div>
          {g.lines.map((r) => <HoursLine key={r.code} code={r.code} name={r.name} budget={r.budget} used={r.used} />)}
          <HoursLine sub name="Subtotal" budget={g.budget} used={g.used} />
        </React.Fragment>
      ))}
      {!groups.length && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--text-3)' }}>No hours budgeted or used yet</div>
      )}
      <HoursLine total name="Total" budget={total.budget} used={total.used} />
    </div>
  );
};
const TravelLine = ({ name, code, budget, used, total }) => {
  const pct = budget > 0 ? used / budget : null;
  const left = budget - used;
  const detail = budget > 0
    ? <>{fmt$(used)} of {fmt$(budget)} · <span style={{ color: left < 0 ? 'var(--danger)' : 'var(--text-2)' }}>{left < 0 ? `${fmt$(-left)} over` : `${fmt$(left)} left`}</span></>
    : used > 0 ? <span style={{ color: 'var(--danger)' }}>{fmt$(used)} used · no budget</span> : 'no budget';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', background: total ? 'var(--bg-surface-2)' : 'transparent' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: total ? 700 : 600, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>{name}</div>
        {code && <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{code}</div>}
      </div>
      <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
        <div className="mono" style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.15, color: pct == null ? 'var(--text-4)' : spentColor(pct), fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{pct == null ? '—' : fmtPct(pct * 100)}</div>
        <div className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{detail}</div>
      </div>
    </div>
  );
};
// Travel codes 370-375 summed across every phase, in Colin's order (also the PSR export).
export const travelByCode = (phases) => {
  const by = {};
  (phases || []).forEach((p) => (p.codes || []).forEach((c) => {
    const k = String(c.code || '').trim();
    if (!by[k]) by[k] = { budget: 0, used: 0 };
    by[k].budget += c.budget;
    by[k].used += c.actual_cost;
  }));
  return TRAVEL_CODES.map(([code, name]) => ({ code, name, budget: by[code] ? by[code].budget : 0, used: by[code] ? by[code].used : 0 }));
};
const TravelSummary = ({ id, phases, onSeeAll, onClose }) => {
  const rows = travelByCode(phases);
  const total = rows.reduce((a, r) => ({ budget: a.budget + r.budget, used: a.used + r.used }), { budget: 0, used: 0 });
  // Right-aligned: Travel is the last tile in its row, so a left-anchored popover
  // would run past the grid's right edge.
  return (
    <div id={id} role="dialog" aria-label="Travel cost summary" tabIndex={-1} style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40, width: 420, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 14px', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
          Travel cost summary <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>· used of budget</span>
        </span>
        <button type="button" aria-label="Close" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      {rows.map((r) => <TravelLine key={r.code} name={r.name} code={r.code} budget={r.budget} used={r.used} />)}
      <TravelLine total name="Total" budget={total.budget} used={total.used} />
      {onSeeAll && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
          <button type="button" className="jc-kpi-more" onClick={() => { onClose(); onSeeAll(); }}>
            View phase breakdown <JcIcon name="chevron-d" size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

/* Risk drill-down — the codes behind the Risk tile: Savings Risk (363) and the
   Construction Risk family (364, 364A–364H…), the same codes the tile sums
   (RISK_FILTER in server/jobCostQueries.js), each summed across every phase, in code
   order. Laid out like the travel summary: percent used big, dollars small. */
const isRiskCode = (code) => code === '363' || code.startsWith('364');
const riskByCode = (phases) => {
  const by = {};
  (phases || []).forEach((p) => (p.codes || []).forEach((c) => {
    const k = String(c.code || '').trim();
    if (!isRiskCode(k)) return;
    if (!by[k]) by[k] = { name: '', budget: 0, used: 0 };
    // cat falls back to the code itself (or '—') when CE sent no name.
    if (!by[k].name && c.cat && c.cat !== '—' && String(c.cat).trim() !== k) by[k].name = c.cat;
    by[k].budget += c.budget;
    by[k].used += c.actual_cost;
  }));
  return Object.keys(by).sort().map((code) => ({ ...by[code], code, name: by[code].name || `Code ${code}` }));
};
const RiskSummary = ({ id, phases, onSeeAll, onClose }) => {
  const rows = riskByCode(phases);
  const total = rows.reduce((a, r) => ({ budget: a.budget + r.budget, used: a.used + r.used }), { budget: 0, used: 0 });
  // Left-aligned, like Actual Cost: Risk is the second tile in its row.
  return (
    <div id={id} role="dialog" aria-label="Risk summary" tabIndex={-1} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40, width: 420, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 14px', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
          Risk summary <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>· used of budget</span>
        </span>
        <button type="button" aria-label="Close" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      {rows.map((r) => <TravelLine key={r.code} name={r.name} code={r.code} budget={r.budget} used={r.used} />)}
      <TravelLine total name="Total" budget={total.budget} used={total.used} />
      {onSeeAll && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
          <button type="button" className="jc-kpi-more" onClick={() => { onClose(); onSeeAll(); }}>
            View phase breakdown <JcIcon name="chevron-d" size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

const signTone = (v) => (v >= 0 ? 'var(--success)' : 'var(--danger)');

/* Calendar days from today to a 'YYYY-MM-DD' day (negative once it has passed), or null.
   Read as LOCAL midnight — new Date('2027-05-28') is UTC midnight, a day early in Central.
   Shared with the project page's "Days remaining" so the two always agree. */
export const daysUntil = (ymd, now = new Date()) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return null;
  return Math.round((new Date(+m[1], +m[2] - 1, +m[3]) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
};

/* The line under Contract Value: when the job is due. The contract completion date comes
   from the construction schedule (Duncan, 2026-10-04), so this is the schedule's last day;
   until a job has a schedule, the project's End date stands in — the same date, in the same
   order, as the project header and its "Days remaining". endDate: undefined = not loaded,
   null = neither yet, else 'YYYY-MM-DD'. */
const dueSub = (endDate) => {
  if (endDate === undefined) return undefined;
  const days = daysUntil(endDate);
  if (days == null) return <span style={{ color: 'var(--text-4)' }}>Due date: no schedule yet</span>;
  const [y, m, d] = String(endDate).slice(0, 10).split('-').map(Number);
  const plural = (n) => `${n} day${n === 1 ? '' : 's'}`;
  return (
    <>Due {fmtDate(new Date(y, m - 1, d))} · {days > 0 ? `${plural(days)} left` : days === 0 ? 'today' : <span style={{ color: 'var(--danger)' }}>{plural(-days)} over</span>}</>
  );
};

export const KpiCards = ({ fin, phases, onSeeAllPhases, collections, hours, endDate, warranty, contract, project, onOpenSov }) => {
  const f = fin;
  // Contract Value drops down what makes up the contract on file, as the job's top bar does (Duncan 2026-11-03) --
  // for a job with a Summit project and an SOV to compare. "Open SOV tab" goes to the project's SOV tab.
  const navigate = useNavigate();
  const contractBreakdown = useContractBreakdown(project && !warranty && !contract ? project.id : null);
  const openSov = onOpenSov || (project && (project.slug || project.id) ? () => navigate(`/projects/${project.slug || project.id}/sov`) : null);
  // Actual Cost opens "spent by phase", and Travel and Risk open their cost summaries,
  // when the caller has the phases loaded. One open at a time.
  const drillId = useId();
  const [drill, setDrill] = useState(null); // null | { key: 'cost' | 'travel' | 'risk' | 'hours', mode: 'hover' | 'pinned' }
  const drillOpen = drill !== null;
  const hoverTimer = useRef(null);
  const canDrill = Array.isArray(phases) && phases.length > 0;
  const clearHover = () => { if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; } };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  // Hover previews (opens after a beat, closes a beat after leaving); a click pins it
  // and a second click unpins. A pinned one ignores hovering over the other tile.
  const onHoverStart = (key) => {
    clearHover();
    if (drill && (drill.mode === 'pinned' || drill.key === key)) return;
    hoverTimer.current = setTimeout(() => setDrill((d) => (d && d.mode === 'pinned' ? d : { key, mode: 'hover' })), 120);
  };
  const onHoverEnd = (key) => {
    clearHover();
    if (drill && drill.mode === 'hover' && drill.key === key) {
      hoverTimer.current = setTimeout(() => setDrill((d) => (d && d.mode === 'hover' && d.key === key ? null : d)), 180);
    }
  };
  const onToggle = (key) => { clearHover(); setDrill((d) => (d && d.key === key && d.mode === 'pinned' ? null : { key, mode: 'pinned' })); };
  const drillProps = (key, popover) => ({
    onToggle: () => onToggle(key),
    onHoverStart: () => onHoverStart(key),
    onHoverEnd: () => onHoverEnd(key),
    expanded: !!drill && drill.key === key,
    popoverId: drillId,
    popover,
  });
  const closeDrill = () => { clearHover(); setDrill(null); };
  useEffect(() => {
    if (!drillOpen) return undefined;
    const onDown = (e) => { if (!(e.target instanceof Element) || !e.target.closest('[data-kpi-drill]')) setDrill(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [drillOpen]);
  const onKeyDown = (e) => {
    // Esc closes the drill-down and stops there. The Job Costs pop-out also closes on
    // Esc, from a window capture listener that stands down inside [data-owns-escape].
    if (e.key === 'Escape' && drillOpen) { e.stopPropagation(); closeDrill(); }
  };
  const gmDelta = f.actual_gm_pct - f.gm_target;   // actual GM% − target GM%
  const gmDollarDelta = f.actual_gm - f.gm;        // actual GM$ − target GM$
  // "Quiet ledger" restraint: neutral surface cards (no soft-fills); color is
  // reserved for the values that carry meaning (margins + spend health). The two
  // Gross Margin tiles LEAD with the ACTUAL (current reality, colored by sign)
  // and carry the TARGET + delta-vs-target on the sub-line — so one tile shows
  // goal-vs-actual at a glance (was four separate Target/Actual GM tiles). Actual
  // Cost, Risk and Travel lead with the PERCENT of their budget, dollars small on the
  // sub-line (leadership review 2026-09-28: "110% means more than $15.1M"). 7 tiles in
  // a 4-col grid (last row of 3). Cost/Risk/Travel go red over budget.
  // Order per Duncan (2026-07-12), L→R top row first:
  // Contract Value · Actual Cost (vs budget) · GM % · Travel  //  Billing · Collected · Risk · GM $ · Labor Hours
  // (2026-09-30: Billing % and % Collected merged to make room for Labor Hours; GM $ moved under GM %).
  const ofBudget = (cost, budget) => (budget > 0 ? cost / budget : null);
  const travelPct = ofBudget(f.travel_cost, f.travel_budget);
  const riskPct = ofBudget(f.risk_cost, f.risk_budget);
  // % Collected is CE's own ratio — collected ÷ everything CE has invoiced (collected +
  // still open) — not Summit billing, which covers only a handful of jobs. collections:
  // undefined = still loading, null = unavailable (no AR access / AR mirror down).
  const colPct = collections && collections.invoiced > 0 ? collections.collected / collections.invoiced : null;
  // Fully collected (nothing left open, to the cent) reads green. The % is rounded
  // DOWN to 0.1%, so "100.0%" only ever appears when it genuinely is all collected —
  // $7k still open on a $19.7M job would otherwise round up to a white "100.0%".
  const colDone = colPct != null && collections.collected >= collections.invoiced - 0.005;
  const colShown = colPct == null ? null : colDone ? Math.max(100, Math.floor(colPct * 1000) / 10) : Math.floor(colPct * 1000) / 10;
  // Labor hours: undefined = still loading, null = unavailable, [] = none pulled yet.
  const hasHours = Array.isArray(hours) && hours.length > 0;
  const hrs = (hours || []).reduce((a, r) => ({ budget: a.budget + (parseFloat(r.hours_budget) || 0), used: a.used + (parseFloat(r.hours) || 0) }), { budget: 0, used: 0 });
  const hoursPct = hasHours && hrs.budget > 0 ? hrs.used / hrs.budget : null;
  const hoursSub = hours === undefined ? 'loading…'
    : !hasHours ? 'no hours pulled yet'
      : hrs.budget > 0 ? `${fmtHrs(hrs.used)} of ${fmtHrs(hrs.budget)} hrs`
        : `${fmtHrs(hrs.used)} hrs · no hours budget`;
  // Tile faces show whole dollars; their drop-downs (PhaseDrivers, TravelSummary, RiskSummary) keep cents.
  const fmtWhole = (n, opts) => fmt$(n, { ...opts, whole: true });
  const lineSub = (cost, budget, what) =>
    budget > 0 ? `${fmtWhole(cost)} of ${fmtWhole(budget)}` : cost > 0 ? `${fmtWhole(cost)} · no ${what} budget` : `no ${what} budget`;
  const cards = [
    {
      label: 'Contract Value', value: fmtWhole(f.contract_value), sub: dueSub(endDate),
      ...(contractBreakdown ? drillProps('contract', (
        <ContractBreakdownSummary id={drillId} breakdown={contractBreakdown} onClose={closeDrill}
          onOpenSov={openSov ? () => { closeDrill(); openSov(); } : undefined} />
      )) : {}),
    },
    {
      label: 'Actual Cost',
      value: f.pctSpent == null ? '—' : fmtPct(f.pctSpent * 100),
      valueColor: spentColor(f.pctSpent),
      sub: f.budget > 0 ? `${fmtWhole(f.actual_cost)} of ${fmtWhole(f.budget)}` : `${fmtWhole(f.actual_cost)} · no budget`,
      ...(canDrill ? drillProps('cost', <PhaseDrivers id={drillId} phases={phases} onSeeAll={onSeeAllPhases} onClose={closeDrill} />) : {}),
    },
    {
      label: 'Gross Margin %',
      value: fmtPct(f.actual_gm_pct * 100),
      valueColor: signTone(f.actual_gm),
      sub: (
        <>Target {fmtPct(f.gmPct * 100)} · <span style={{ color: signTone(gmDelta) }}>{`${gmDelta >= 0 ? '+' : ''}${fmtPct(gmDelta * 100)}`}</span></>
      ),
    },
    {
      label: 'Travel',
      value: travelPct == null ? '—' : fmtPct(travelPct * 100),
      valueColor: travelPct == null ? 'var(--text-4)' : travelPct > 1 ? 'var(--danger)' : undefined,
      sub: lineSub(f.travel_cost, f.travel_budget, 'travel'),
      ...(canDrill && (f.travel_budget > 0 || f.travel_cost > 0)
        ? drillProps('travel', <TravelSummary id={drillId} phases={phases} onSeeAll={onSeeAllPhases} onClose={closeDrill} />) : {}),
    },
    {
      // Billing % Complete and % Collected share a tile (freeing one for Labor Hours):
      // collected — CE's own ratio, right for every job — leads; billed below (the project
      // strip's figure: Summit pay apps or CE's invoices, whichever is further along).
      label: 'Billing · Collected',
      value: colShown == null ? '—' : <>{fmtPct(colShown)}<span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0, color: 'var(--text-3)', marginLeft: 6 }}>collected</span></>,
      valueColor: colShown == null ? 'var(--text-4)' : colDone ? 'var(--success)' : undefined,
      sub: `${fmtPct(f.billing_pct * 100)} billed · ${fmtWhole(f.billed)}`,
    },
    {
      label: 'Risk',
      value: riskPct == null ? '—' : fmtPct(riskPct * 100),
      valueColor: riskPct == null ? 'var(--text-4)' : riskPct > 1 ? 'var(--danger)' : undefined,
      sub: lineSub(f.risk_cost, f.risk_budget, 'risk'),
      ...(canDrill && (f.risk_budget > 0 || f.risk_cost > 0)
        ? drillProps('risk', <RiskSummary id={drillId} phases={phases} onSeeAll={onSeeAllPhases} onClose={closeDrill} />) : {}),
    },
    {
      label: 'Gross Margin $',
      value: fmtWhole(f.actual_gm, { signed: true }),
      valueColor: signTone(f.actual_gm),
      sub: (
        <>Target {fmtWhole(f.gm)} · <span style={{ color: signTone(gmDollarDelta) }}>{fmtWhole(gmDollarDelta, { signed: true })}</span></>
      ),
    },
    {
      label: 'Labor Hours',
      value: hoursPct == null ? '—' : fmtPct(hoursPct * 100),
      valueColor: hoursPct == null ? 'var(--text-4)' : hoursPct > 1 ? 'var(--danger)' : undefined,
      sub: hoursSub,
      ...(hasHours ? drillProps('hours', <HoursSummary id={drillId} rows={hours} phases={phases} onClose={closeDrill} />) : {}),
    },
  ];
  // A W job has a warranty budget and no contract, so the contract, billing and margin tiles give
  // way to the budget and what's left of it (the Warranty view's own tiles, job by job):
  // Warranty Budget · Actual Cost · Committed · Left  //  Travel · Risk · Labor Hours.
  const left = f.budget - f.actual_cost - f.committed;
  const tile = (label) => cards.find((c) => c.label === label);
  const shown = !warranty ? cards : [
    { label: 'Warranty Budget', value: fmtWhole(f.budget), sub: 'warranty · no contract' },
    tile('Actual Cost'),
    { label: 'Committed', value: f.committed ? fmtWhole(f.committed) : '—', valueColor: f.committed ? 'var(--warning)' : 'var(--text-4)', sub: f.committed ? 'open POs' : 'no open POs' },
    { label: 'Left', value: fmtWhole(left), valueColor: left < -0.5 ? 'var(--danger)' : 'var(--success)', sub: left < -0.5 ? 'over the warranty budget' : 'of the warranty budget' },
    tile('Travel'), tile('Risk'), tile('Labor Hours'),
  ];
  return (
    <div onKeyDown={onKeyDown} data-owns-escape={drillOpen ? 'true' : undefined} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
      {(canDrill || contractBreakdown) && <style>{KPI_DRILL_CSS}</style>}
      {shown.map((c) => (contract && c.label === 'Contract Value'
        ? <ContractValueTile key={c.label} contract={contract} />
        : <KpiCard key={c.label} {...c} />))}
    </div>
  );
};

/* ─────────────── Monthly Cost Trend — sage SVG bar chart ─────────────── */

const BILLED_FILL = 'color-mix(in oklch, var(--text-3) 30%, transparent)';
const BILLED_RING = 'color-mix(in oklch, var(--text-3) 60%, transparent)';
const HOURS_LINE = 'var(--warning)';
const fmtHrsAxis = (v) => `${Math.round(v).toLocaleString('en-US')} h`;
// Round a max up to a readable scale top (1, 2, 2.5, 5 × 10^n).
const niceCeil = (v) => {
  if (!(v > 0)) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
};

/* Legend for the trend chart header — only the series this job actually has. */
export const MonthlyLegend = ({ monthly }) => {
  const hasBilled = (monthly || []).some((d) => d.billed != null);
  const hasHours = (monthly || []).some((d) => d.hours != null);
  const item = (swatch, text) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{swatch}<span>{text}</span></span>
  );
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, fontSize: 12.5, color: 'var(--text-3)' }}>
      {item(<span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--accent)' }} />, 'Spent')}
      {hasBilled && item(<span style={{ width: 10, height: 10, borderRadius: 2, background: BILLED_FILL, border: `1px solid ${BILLED_RING}` }} />, 'Billed (CE)')}
      {hasHours && item(<span style={{ width: 14, height: 0, borderTop: `2px solid ${HOURS_LINE}` }} />, 'Hours')}
      <span className="mono" style={{ color: 'var(--text-4)' }}>per month</span>
    </span>
  );
};

/* Month pop-out — the same dark floating card as the Billings Projection charts.
   It opens BESIDE the hovered month (flipping left near the right edge): the chart
   scrolls sideways inside a strip that clips anything above it. */
const MonthTip = ({ x, top, title, rows, width, tw = 214 }) => {
  const left = x + tw + 12 > width ? Math.max(4, x - tw - 12) : x + 12;
  return (
    <div style={{ position: 'absolute', left, top, width: tw, pointerEvents: 'none', zIndex: 6, background: 'var(--text-1)', borderRadius: 9, boxShadow: '0 12px 28px -8px oklch(0.2 0.02 100 / 0.45)', padding: '10px 13px' }}>
      <div className="mono" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'color-mix(in oklch, var(--bg-page) 62%, transparent)', marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <span style={{ width: 8, height: r.line ? 0 : 8, borderRadius: 2, flex: '0 0 auto', ...(r.line ? { borderTop: `2px solid ${r.swatch}` } : { background: r.swatch }) }} />
              <span style={{ fontSize: 13, color: 'color-mix(in oklch, var(--bg-page) 78%, transparent)', whiteSpace: 'nowrap' }}>{r.label}</span>
            </div>
            <span className="mono" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--bg-page)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.015em', flex: '0 0 auto' }}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MonthlyChart = ({ monthly }) => {
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  const [hover, setHover] = useState(null); // index of the hovered month
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setWrapW(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!monthly || monthly.length === 0) return <div style={{ padding: 16, textAlign: 'center', fontSize: 13.5, color: 'var(--text-4)' }}>No monthly cost data.</div>;

  const hasBilled = monthly.some((d) => d.billed != null);
  const hasHours = monthly.some((d) => d.hours != null);
  // Hours get their own scale on the LEFT; dollars (spent + billed) stay on the right.
  const H = 220, padL = hasHours ? 52 : 16, padR = 56, padT = 16, padB = 30;
  const plotH = H - padT - padB;
  const n = monthly.length || 1;
  // Every month fits the box, however long the job (#9 — no sideways scroll): the
  // months share the width, so a long job gets thinner bars. Floored so the SVG never
  // overshoots the box by a fraction of a pixel and draws a scrollbar.
  const plotArea = Math.max(Math.floor((wrapW || 0) - padL - padR), 120);
  const step = plotArea / n;
  const plotW = step * n;
  const W = plotW + padL + padR;
  // With billed beside it, each month holds two bars.
  const barW = hasBilled ? Math.min(26, step * 0.3) : Math.min(60, step * 0.5);
  const pairGap = Math.min(3, step * 0.1);
  const dotR = Math.min(3.2, Math.max(1.6, step * 0.22));
  // Month labels need ~46px ("Nov '23"), so a long job labels whole calendar steps
  // instead — every 2nd month, quarter, half-year or January — counted from January.
  // The hovered month is always labelled, and neighbours it would touch step aside.
  const LABEL_W = 46;
  const labelEvery = [1, 2, 3, 6, 12].find((k) => k * step >= LABEL_W) || 12 * Math.ceil(LABEL_W / (12 * step));
  const showLabel = (d, i) => (hover != null && i !== hover && Math.abs(i - hover) * step < LABEL_W
    ? false
    : i === hover || (+String(d.key).slice(5, 7) - 1) % labelEvery === 0);
  const rawMax = Math.max(...monthly.map((d) => Math.max(d.cost, d.billed || 0)), 1);
  const niceMax = Math.ceil(rawMax / 1e5) * 1e5 || 1e5;
  const hMax = niceCeil(Math.max(...monthly.map((d) => d.hours || 0), 1));
  const cx = (i) => padL + step * (i + 0.5);
  const by = (v) => padT + plotH - (v / niceMax) * plotH;
  const hy = (v) => padT + plotH - (Math.max(0, v) / hMax) * plotH;
  const grid = [0, 1, 2, 3, 4];
  const spendX = (i) => (hasBilled ? cx(i) - pairGap / 2 - barW : cx(i) - barW / 2);
  // Hours line: one segment per unbroken run of months that have hours.
  const runs = [];
  monthly.forEach((d, i) => {
    if (d.hours == null) return;
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === i - 1) last.push(i); else runs.push([i]);
  });
  const hd = hover != null ? monthly[hover] : null;
  const tipRows = hd ? [
    { label: 'Spent', value: fmt$(hd.cost), swatch: 'var(--accent)' },
    ...(hasBilled ? [{ label: 'Billed (CE)', value: fmt$(hd.billed || 0), swatch: BILLED_RING }] : []),
    ...(hasHours ? [{ label: 'Hours', value: hd.hours == null ? '—' : `${fmtHrs(hd.hours)} hrs`, swatch: HOURS_LINE, line: true }] : []),
  ] : [];
  return (
    <div ref={wrapRef} style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <div style={{ position: 'relative', width: W }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} onMouseLeave={() => setHover(null)}>
          <defs>
            <linearGradient id="jcBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="color-mix(in oklch, var(--accent) 78%, transparent)" />
              <stop offset="100%" stopColor="color-mix(in oklch, var(--accent) 40%, transparent)" />
            </linearGradient>
          </defs>
          {hover != null && <rect x={padL + step * hover} y={padT} width={step} height={plotH} rx="4" fill="color-mix(in oklch, var(--accent) 8%, transparent)" />}
          {grid.map((g) => {
            const y = padT + (plotH * g) / 4;
            const v = niceMax * (1 - g / 4);
            return (
              <g key={g}>
                <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray={g === 4 ? '0' : '2 3'} />
                <text x={padL + plotW + 8} y={y + 3.5} className="mono" fontSize="9.5" fill="var(--text-4)" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt$c(v)}</text>
                {hasHours && <text x={padL - 8} y={y + 3.5} textAnchor="end" className="mono" fontSize="9.5" fill={HOURS_LINE} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtHrsAxis(hMax * (1 - g / 4))}</text>}
              </g>
            );
          })}
          {monthly.map((d, i) => {
            const h = (d.cost / niceMax) * plotH, x = spendX(i), y = by(d.cost);
            const bh = d.billed != null ? (Math.max(0, d.billed) / niceMax) * plotH : 0;
            return (
              <g key={i}>
                <rect x={x} y={y} width={barW} height={Math.max(0, h)} rx="3" fill="url(#jcBar)" stroke={ring('var(--accent)')} strokeWidth="1" />
                {d.billed != null && (
                  <rect x={cx(i) + pairGap / 2} y={by(Math.max(0, d.billed))} width={barW} height={Math.max(0, bh)} rx="3" fill={BILLED_FILL} stroke={BILLED_RING} strokeWidth="1" />
                )}
                {showLabel(d, i) && <text x={cx(i)} y={H - 10} textAnchor="middle" fontSize="10.5" fontWeight={hover === i ? 700 : 400} fill={hover === i ? 'var(--text-1)' : 'var(--text-3)'}>{d.month}</text>}
              </g>
            );
          })}
          {runs.map((run, ri) => (
            <g key={`h${ri}`}>
              {run.length > 1 && <polyline points={run.map((i) => `${cx(i)},${hy(monthly[i].hours)}`).join(' ')} fill="none" stroke={HOURS_LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
              {run.map((i) => <circle key={i} cx={cx(i)} cy={hy(monthly[i].hours)} r={hover === i ? dotR + 1.8 : dotR} fill="var(--bg-surface)" stroke={HOURS_LINE} strokeWidth="2" />)}
            </g>
          ))}
          {/* Hover targets: the whole month column, on top of everything. */}
          {monthly.map((d, i) => (
            <rect key={`t${i}`} x={padL + step * i} y={0} width={step} height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hd && <MonthTip x={cx(hover)} top={padT + 4} width={W} title={hd.month} rows={tipRows} />}
      </div>
    </div>
  );
};

/* Under the chart's box (not inside it — no dead band in the plot): what "Billed" means. */
export const MonthlyNote = ({ monthly }) => ((monthly || []).some((d) => d.billed != null) ? (
  <div style={{ margin: '6px 2px 0', fontSize: 12, lineHeight: 1.4, color: 'var(--text-4)' }}>
    Billed (CE) = invoices by their CE invoice date, not the month the work was done.
  </div>
) : null);

/* ─────────────── note indicator + popover ─────────────── */

const NoteIndicator = ({ notes, has, onClick }) => (
  <button
    type="button"
    className={`j-note${has ? ' has' : ''}`}
    onClick={onClick}
    title={has ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : 'Add a note'}
    style={{ width: 26, height: 26, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: has ? 'var(--accent-text)' : 'var(--text-4)', background: has ? 'var(--accent-soft)' : 'transparent', border: `1px solid ${has ? ring('var(--accent)') : 'transparent'}`, position: 'relative' }}
  >
    <JcIcon name={has ? 'note' : 'note-plus'} size={14} sw={1.7} />
    {has && (
      <span className="mono" style={{ position: 'absolute', top: -5, right: -5, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 999, background: 'var(--accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid var(--bg-surface)' }}>{notes.length}</span>
    )}
  </button>
);

const NotePopover = ({ notes, pos, codeLabel, onAdd, onClose }) => {
  const [draft, setDraft] = useState('');
  const taRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (taRef.current) taRef.current.focus(); }, []);
  const submit = async () => {
    const txt = draft.trim();
    if (!txt || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(txt);
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: pos.top, right: pos.right, zIndex: 40, width: 320, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
        <span style={{ color: 'var(--accent-text)', display: 'inline-flex' }}><JcIcon name="note" size={14} /></span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{codeLabel}</span>
        <button type="button" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
          <JcIcon name="x" size={13} sw={2} />
        </button>
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', padding: notes.length ? '4px 0' : 0 }}>
        {notes.length === 0 ? (
          <div style={{ padding: '18px 14px', fontSize: 14, color: 'var(--text-3)', textAlign: 'center' }}>No notes yet on this cost code.</div>
        ) : (
          notes.map((nt, i) => (
            <div key={i} style={{ padding: '9px 13px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{nt.author}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', whiteSpace: 'nowrap' }}>{fmtDate(nt.date)}</span>
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{nt.text}</div>
            </div>
          ))
        )}
      </div>
      <div style={{ padding: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
          placeholder="Add a note…"
          rows={2}
          className="j-note-ta"
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 46, padding: '8px 10px', borderRadius: 8, fontFamily: 'var(--font-sans)', fontSize: 14, color: 'var(--text-1)', lineHeight: 1.5, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', outline: 'none' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || submitting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 13px', borderRadius: 7, fontSize: 14, fontWeight: 600, color: '#fff', background: draft.trim() ? 'var(--accent)' : 'color-mix(in oklch, var(--accent) 45%, var(--bg-surface-2))', border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))', boxShadow: draft.trim() ? '0 1px 0 oklch(0.42 0.06 135)' : 'none', cursor: draft.trim() && !submitting ? 'pointer' : 'default' }}
          >
            <JcIcon name="note-plus" size={13} sw={1.9} /> {submitting ? 'Adding…' : 'Add note'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Phase Breakdown table ─────────────── */

const GRID = '2fr 1fr 1fr 1fr 1fr 1fr 44px';

const Cell = ({ children, col, colHover, align = 'right', style = {} }) => {
  const tinted = col >= 1 && col <= 5 && col === colHover;
  return (
    <div data-col={col} style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start', padding: '0 12px', minWidth: 0, background: tinted ? 'color-mix(in oklch, var(--accent) 6%, transparent)' : 'transparent', ...style }}>
      {children}
    </div>
  );
};

const Money = ({ v, color, bold, dash }) => (
  <span className="mono" style={{ fontSize: 14, fontWeight: bold ? 700 : 500, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{dash ? '–' : fmt$(v)}</span>
);

const HeadCell = ({ children, align = 'right', col, colHover }) => {
  const tinted = col >= 1 && col <= 5 && col === colHover;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', padding: '0 12px', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', background: tinted ? 'color-mix(in oklch, var(--accent) 7%, var(--bg-surface-2))' : 'var(--bg-surface-2)', whiteSpace: 'nowrap' }}>{children}</div>
  );
};

export const PhaseTable = ({ phases, jobId, getNotes, addNote }) => {
  const [colHover, setColHover] = useState(null);
  const [rowHover, setRowHover] = useState(null);
  const [openNote, setOpenNote] = useState(null); // { phaseNo, code, label }
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const cardRef = useRef(null);

  const rowProps = (id) => ({
    onMouseEnter: () => setRowHover(id),
    onMouseMove: (e) => { const c = e.target.closest('[data-col]'); if (c) setColHover(Number(c.getAttribute('data-col'))); },
  });

  const openNoteAt = (btn, meta) => {
    const r = btn.getBoundingClientRect();
    const cr = cardRef.current.getBoundingClientRect();
    setPos({ top: r.bottom - cr.top + 6, right: cr.right - r.right - 4 });
    setOpenNote(meta);
  };

  const totals = (phases || []).reduce((a, p) => ({ budget: a.budget + p.budget, cost: a.cost + p.actual_cost, committed: a.committed + p.committed, over: a.over + p.over_under }), { budget: 0, cost: 0, committed: 0, over: 0 });
  const totalSpent = totals.budget ? totals.cost / totals.budget : null;
  const ROWH = 44;

  return (
    <div ref={cardRef} style={{ position: 'relative' }} onMouseLeave={() => { setColHover(null); setRowHover(null); }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 880 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, height: 38, position: 'sticky', top: 0, zIndex: 3, borderBottom: '1px solid var(--border-default)' }}>
              <HeadCell align="left" col={0}>Phase / Cost Code</HeadCell>
              <HeadCell col={1} colHover={colHover}>Budget</HeadCell>
              <HeadCell col={2} colHover={colHover}>Actual Cost</HeadCell>
              <HeadCell col={3} colHover={colHover}>Committed</HeadCell>
              <HeadCell col={4} colHover={colHover}>Over / Under</HeadCell>
              <HeadCell col={5} colHover={colHover}>% Spent</HeadCell>
              <HeadCell col={6} align="left"> </HeadCell>
            </div>

            {(phases || []).map((ph) => {
              const pPct = ph.budget ? ph.actual_cost / ph.budget : null;
              return (
                <React.Fragment key={ph.no}>
                  <div {...rowProps(`ph-${ph.no}`)} style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: ROWH, background: 'color-mix(in oklch, var(--accent) 8%, var(--bg-surface))', borderBottom: '1px solid var(--border-subtle)' }}>
                    <Cell col={0} align="left" colHover={colHover}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, padding: '7px 0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ph.name}</span>
                          <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>Phase {ph.no} · {ph.codes.length} cost codes</span>
                        </div>
                        <HealthBadge pct={pPct} size="sm" />
                      </div>
                    </Cell>
                    <Cell col={1} colHover={colHover}><Money v={ph.budget} bold /></Cell>
                    <Cell col={2} colHover={colHover}><Money v={ph.actual_cost} bold color={spentColor(pPct)} /></Cell>
                    <Cell col={3} colHover={colHover}><Money v={ph.committed} bold color={ph.committed ? 'var(--warning)' : 'var(--text-4)'} /></Cell>
                    <Cell col={4} colHover={colHover}><Money v={ph.over_under} bold color={overTone(ph.over_under)} /></Cell>
                    <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14, fontWeight: 700, color: spentColor(pPct), fontVariantNumeric: 'tabular-nums' }}>{pPct == null ? '–' : fmtPct(pPct * 100)}</span></Cell>
                    <Cell col={6} colHover={colHover}> </Cell>
                  </div>

                  {ph.codes.map((cd) => {
                    const cPct = cd.budget ? cd.actual_cost / cd.budget : null;
                    const rid = `c-${ph.no}-${cd.code}`;
                    const notes = getNotes(ph.no, cd.code);
                    const has = notes.length > 0;
                    const isOpen = openNote && openNote.phaseNo === ph.no && openNote.code === cd.code;
                    return (
                      <div key={cd.code} {...rowProps(rid)} style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: ROWH, background: rowHover === rid ? 'var(--bg-hover)' : 'transparent', borderBottom: '1px solid var(--border-subtle)' }}>
                        <Cell col={0} align="left" colHover={colHover}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, paddingLeft: 14 }}>
                            <span style={{ width: 5, height: 5, borderRadius: 99, background: 'var(--border-strong)', flex: '0 0 auto' }} />
                            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cd.cat}</span>
                            {cd.code && <span className="mono" style={{ fontSize: 12, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{cd.code}</span>}
                            {cd.assumed && <span style={{ display: 'inline-flex', alignItems: 'center', height: 16, padding: '0 6px', borderRadius: 5, flex: '0 0 auto', fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: 'var(--accent-text)', background: 'var(--accent-soft)', border: `1px solid ${ring('var(--accent)')}` }}>assumed</span>}
                          </div>
                        </Cell>
                        <Cell col={1} colHover={colHover}><Money v={cd.budget} dash={!cd.budget} /></Cell>
                        <Cell col={2} colHover={colHover}><Money v={cd.actual_cost} color={spentColor(cPct)} /></Cell>
                        <Cell col={3} colHover={colHover}><Money v={cd.committed} color={cd.committed ? 'var(--warning)' : 'var(--text-4)'} dash={!cd.committed} /></Cell>
                        <Cell col={4} colHover={colHover}><Money v={cd.over_under} color={cd.over_under > 0.5 ? 'var(--danger)' : 'var(--text-3)'} /></Cell>
                        <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14, fontWeight: 600, color: spentColor(cPct), fontVariantNumeric: 'tabular-nums' }}>{cPct == null ? '–' : fmtPct(cPct * 100)}</span></Cell>
                        <Cell col={6} align="center" colHover={colHover}>
                          <NoteIndicator
                            notes={notes}
                            has={has}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isOpen) setOpenNote(null);
                              else openNoteAt(e.currentTarget, { phaseNo: ph.no, code: cd.code, label: `${cd.cat} · ${cd.code}` });
                            }}
                          />
                        </Cell>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <div style={{ display: 'grid', gridTemplateColumns: GRID, minHeight: 46, borderTop: '2px solid var(--border-default)', background: 'var(--bg-surface-2)' }}>
              <Cell col={0} align="left"><span style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Total</span></Cell>
              <Cell col={1} colHover={colHover}><Money v={totals.budget} bold /></Cell>
              <Cell col={2} colHover={colHover}><Money v={totals.cost} bold color={spentColor(totalSpent)} /></Cell>
              <Cell col={3} colHover={colHover}><Money v={totals.committed} bold color={totals.committed ? 'var(--warning)' : 'var(--text-4)'} /></Cell>
              <Cell col={4} colHover={colHover}><Money v={totals.over} bold color={overTone(totals.over)} /></Cell>
              <Cell col={5} colHover={colHover}><span className="mono" style={{ fontSize: 14.5, fontWeight: 800, color: spentColor(totalSpent), fontVariantNumeric: 'tabular-nums' }}>{totalSpent == null ? '–' : fmtPct(totalSpent * 100)}</span></Cell>
              <Cell col={6}> </Cell>
            </div>
          </div>
        </div>
      </div>

      {openNote && (
        <NotePopover
          notes={getNotes(openNote.phaseNo, openNote.code)}
          pos={pos}
          codeLabel={openNote.label}
          onAdd={(txt) => addNote(openNote.phaseNo, openNote.code, txt)}
          onClose={() => setOpenNote(null)}
        />
      )}
    </div>
  );
};

/* ─────────────── Budget Utilization bar ─────────────── */

/* Segment-label fit test. The in-bar labels used to be gated on the segment's
   PERCENTAGE of the bar alone, which is width-blind: at the dashboard's narrow
   190px bar column, `commW >= 14` passes at 26.6px while "$1.2M" needs 34.8px, so
   the label rendered clipped to an unreadable stub instead of being dropped. We
   measure the bar and require the segment to be wide enough in ACTUAL pixels too.
   Geist Mono advances 0.600em and letter-spacing is -0.02em, so a 12px label costs
   ~6.96px/char and a 12.5px one ~7.25px/char. */
const labelFitsSegment = (barPx, segPct, text, size, pad) =>
  barPx == null || (segPct / 100) * barPx >= text.length * size * 0.58 + pad;

/* Below this bar width the caption sheds its "$15.3M of $15.1M" token. That token
   is the weakest of the caption's four: the "$15.3M" half is already printed inside
   the bar a few pixels above it, and both halves exist at full cents in the row's
   own Budget/Spent columns. Shedding it is what stops the caption wrapping to four
   and five lines on a laptop (which inflated every job card by 22–46px). The
   project-detail Job Costs tab gives the bar >= 240px, so it keeps the token. */
const CAPTION_TIGHT_PX = 240;

export const BudgetBar = ({ budget, cost, committed, caption = true, height = 24 }) => {
  const denom = Math.max(budget, cost + committed, 1);
  const costW = (cost / denom) * 100;
  const commW = (committed / denom) * 100;
  const budgetX = (budget / denom) * 100;
  const filledW = Math.min(costW + commW, 100);
  const remaining = Math.max(budget - cost - committed, 0);
  const remainW = Math.max(Math.min(budgetX, 100) - filledW, 0);
  const pct = budget ? cost / budget : null;
  const tone = healthTone(pct);
  const over = pct != null && pct > 1.00005;

  // Measure the rendered bar so the label gates above can use real pixels. Until
  // the first measurement lands barPx is null, which makes every gate fall back to
  // the old percentage-only behaviour — never a blank bar on the first paint.
  const barRef = useRef(null);
  const [barPx, setBarPx] = useState(null);
  // useLayoutEffect, not useEffect: measure before the browser paints, so the caption
  // never shows its wide form for a frame and then sheds a token. All 63 bars read in
  // the same layout pass, so this costs one reflow, not 63. The ResizeObserver then
  // keeps it correct as the window changes — which is the whole point of the gate.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    setBarPx(el.getBoundingClientRect().width || null);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 0;
      if (w) setBarPx(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const costTxt = fmt$c(cost);
  const commTxt = fmt$c(committed);
  const remTxt = fmt$c(remaining);
  const showCost = costW >= 22 && labelFitsSegment(barPx, costW, costTxt, 12.5, 10);
  const showComm = commW >= 14 && labelFitsSegment(barPx, commW, commTxt, 12, 8);
  const showRem = remainW >= 20 && labelFitsSegment(barPx, remainW, remTxt, 12, 10);
  const showRemLong = remainW >= 33 && labelFitsSegment(barPx, remainW, remTxt + ' left', 12, 10);
  const captionTight = barPx != null && barPx < CAPTION_TIGHT_PX;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div ref={barRef} style={{ position: 'relative', height, borderRadius: 6, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(costW, 100)}%`, background: over ? 'repeating-linear-gradient(45deg, var(--danger) 0 7px, color-mix(in oklch, var(--danger) 70%, var(--bg-surface)) 7px 14px)' : tone, display: 'flex', alignItems: 'center', paddingLeft: 8, overflow: 'hidden' }}>
          {showCost && <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', textShadow: '0 1px 1px oklch(0 0 0 / 0.18)', whiteSpace: 'nowrap' }}>{costTxt}</span>}
        </div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(costW, 100)}%`, width: `${commW}%`, background: 'repeating-linear-gradient(45deg, color-mix(in oklch, var(--warning) 55%, var(--bg-surface-2)) 0 6px, color-mix(in oklch, var(--warning) 32%, var(--bg-surface-2)) 6px 12px)', borderLeft: '1px solid var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {showComm && <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{commTxt}</span>}
        </div>
        {/* Remaining budget — an explicit filled segment + label so the headroom
            reads clearly instead of blending into the gray track/background. */}
        {remaining > 0 && remainW > 0 && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${filledW}%`, width: `${remainW}%`, background: 'color-mix(in oklch, var(--text-3) 14%, var(--bg-surface-2))', borderLeft: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8, overflow: 'hidden' }}>
            {/* Never wrap. Drop the " left" suffix when the segment is too narrow
                for the full label (the caption below always spells out "left"). */}
            {showRem && <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{remTxt}{showRemLong ? ' left' : ''}</span>}
          </div>
        )}
        {budgetX < 99.5 && <div style={{ position: 'absolute', top: -1, bottom: -1, left: `${budgetX}%`, width: 0, borderLeft: '2px dashed var(--text-2)', zIndex: 2 }} />}
      </div>
      {caption && (
        <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}>
          <span style={{ color: spentColor(pct), fontWeight: 700 }}>{pct == null ? '–' : fmtPct(pct * 100)} spent</span>
          {!captionTight && <><span style={{ color: 'var(--text-4)' }}>·</span><span>{fmt$c(cost)} of {fmt$c(budget)}</span></>}
          {committed > 0 && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ color: 'var(--warning)' }}>{fmt$c(committed)} committed</span></>}
          {remaining > 0 && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{fmt$c(remaining)} left</span></>}
        </div>
      )}
    </div>
  );
};

/* ─────────────── Billing %-complete bar ─────────────── */

export const BillingBar = ({ pct, billed, contract, height = 8 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <div style={{ height, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(pct * 100, 100)}%`, background: 'var(--accent)', borderRadius: 999 }} />
    </div>
    <div className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ color: 'var(--accent-text)', fontWeight: 700 }}>{fmtPct(pct * 100)}</span> · {fmt$c(billed)} of {fmt$c(contract)}
    </div>
  </div>
);

/* ─────────────── % Spent progress ring ─────────────── */

export const SpentRing = ({ pct, size = 64 }) => {
  const tone = spentColor(pct);
  const R = (size - 8) / 2, C = 2 * Math.PI * R;
  const frac = pct == null ? 0 : Math.min(pct, 1);
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke="var(--bg-surface-2)" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={R} fill="none" stroke={tone} strokeWidth="5" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - frac)} />
      </svg>
      <span className="mono" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.23, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em' }}>{pct == null ? '–' : `${Math.round(pct * 100)}%`}</span>
    </div>
  );
};

/* ─────────────── synced chip ─────────────── */

export const SyncChip = ({ label = 'Synced from ComputerEase', when = 'just now' }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 30, padding: '0 12px 0 10px', borderRadius: 999, background: 'var(--accent-soft)', border: `1px solid ${ring('var(--accent)')}`, color: 'var(--accent-text)', boxShadow: 'var(--shadow-sm)' }}>
    <span style={{ display: 'inline-flex' }}><JcIcon name="sync" size={14} sw={1.9} /></span>
    <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }}>{label}</span>
    <span className="mono" style={{ fontSize: 12.5, color: 'color-mix(in oklch, var(--accent-text) 70%, transparent)', fontVariantNumeric: 'tabular-nums' }}>{when}</span>
  </div>
);

/* A W job holds a finished job's warranty budget (Duncan 2026-10-19: "when a job ends, the warranty
   budget get transfered to a 'W' job") -- Job Costs → Warranty. No contract, so no margin. */
export const isWarrantyJob = (id) => /-W-/i.test(String(id || ''));

/* ─────────────── shared financials builder ─────────────── */

// totals: { total_budget, total_cost, total_committed } from the CE summary.
// project: the linked project row (revised_contract_value / contract_amount / billing).
export const buildFinancials = (totals, project) => {
  // A P / E / M job has no project page: its contract is set in Job Costs (+ CE change orders) and
  // rides on the job's row as contract_value (JobCostDashboardPage).
  const contract_value = parseFloat(project?.revised_contract_value || project?.contract_amount || totals?.contract_value || 0);
  const budget = parseFloat(totals?.total_budget || 0);
  const cost = parseFloat(totals?.total_cost || 0);
  const committed = parseFloat(totals?.total_committed || 0);
  // Summit pay apps or CE's invoices, whichever is further along: the project strip's
  // figure (#63, server/ceBilling.js). A row without `billing` falls back to Summit's.
  const billed = project?.billing ? Number(project.billing.billed) || 0 : parseFloat(project?.total_billed || 0);
  const pctSpent = budget > 0 ? cost / budget : null;
  const gm = contract_value - budget;
  const gmPct = contract_value > 0 ? gm / contract_value : 0;
  const actual_gm = contract_value - (cost + committed);
  const actual_gm_pct = contract_value > 0 ? actual_gm / contract_value : 0;
  return {
    contract_value, budget, actual_cost: cost, committed,
    over_under: cost - budget, pctSpent,
    billed, billing_pct: contract_value > 0 ? billed / contract_value : 0,
    gm, gmPct, gm_target: gmPct, actual_gm, actual_gm_pct,
    risk_budget: parseFloat(totals?.risk_budget || 0),
    risk_cost: parseFloat(totals?.risk_cost || 0),
    travel_budget: parseFloat(totals?.travel_budget || 0),
    travel_cost: parseFloat(totals?.travel_cost || 0),
  };
};
